import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Stack, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, TextField,
  CircularProgress, Chip, Divider, Tooltip,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { tenantApi } from '../../api/tenantApi';

const REPORT_TYPES = [
  { value: 'inventory_snapshot',  label: 'Inventory Snapshot', desc: 'All current inventory levels' },
  { value: 'low_stock',           label: 'Low Stock Report',   desc: 'Items below reorder threshold' },
  { value: 'valuation',           label: 'Inventory Valuation', desc: 'Cost and value by item' },
  { value: 'advanced_analytics',  label: 'Advanced Analytics', desc: 'Value & stock health by category, with totals' },
  { value: 'audit_log',           label: 'Audit Log Export',   desc: 'Full change history (every user, inventory & account change)' },
  { value: 'transaction_history', label: 'Transaction History', desc: 'All stock movements over time' },
];

const FORMATS = [
  { value: 'csv',   label: 'CSV' },
  { value: 'excel', label: 'Excel (.xlsx)' },
  { value: 'pdf',   label: 'PDF' },
];

// Feature flag that gates each export format.
const FORMAT_FEATURE = {
  csv:   'reporting.export_csv',
  excel: 'reporting.export_excel',
  pdf:   'reporting.export_pdf',
};

// Feature flag that gates an entire report type (others are always available).
const TYPE_FEATURE = {
  valuation: 'reporting.inventory_valuation_reports',
  advanced_analytics: 'reporting.advanced_analytics',
  audit_log: 'reporting.audit_log_export',
};

const TYPE_FEATURE_LABEL = {
  valuation: 'Inventory Valuation Reports',
  advanced_analytics: 'Advanced Analytics',
  audit_log: 'Audit Log Export',
};

const SCHEDULE_FEATURE = 'reporting.scheduled_reports';

// Force a browser download (the presigned URL also sends Content-Disposition:
// attachment, so the file is saved rather than opened in a new tab).
function triggerDownload(url) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function ReportsPage() {
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedForm, setSchedForm] = useState({ name: '', type: 'inventory_snapshot', format: 'csv', cronExpression: '0 8 * * MON', recipients: '' });
  const [busyKey, setBusyKey] = useState(null); // `${type}:${format}` currently generating
  const [features, setFeatures] = useState(null); // null = still loading
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    tenantApi.getMyFeatures()
      .then((data) => setFeatures(data.features || data || {}))
      .catch(() => setFeatures({}));
  }, []);

  const featureOn = (key) => Boolean(features && features[key]);
  const typeEnabled = (type) => (TYPE_FEATURE[type] ? featureOn(TYPE_FEATURE[type]) : true);
  const formatEnabled = (type, fmt) => typeEnabled(type) && featureOn(FORMAT_FEATURE[fmt]);

  const disabledReason = (type, fmt) => {
    if (!typeEnabled(type)) { return `Requires the ${TYPE_FEATURE_LABEL[type] || 'required'} feature on your plan.`; }
    if (!featureOn(FORMAT_FEATURE[fmt])) { return `${fmt.toUpperCase()} export is not enabled on your plan.`; }
    return '';
  };

  const handleGenerate = async (type, format) => {
    const key = `${type}:${format}`;
    setBusyKey(key);
    try {
      const res = await tenantApi.generateReport({ type, format });
      if (res.downloadUrl) {
        triggerDownload(res.downloadUrl);
        setToast({ open: true, message: `${res.filename || 'Report'} downloaded (${res.rowCount ?? 0} rows).`, severity: 'success' });
      } else {
        setToast({ open: true, message: 'Report generated but no download URL was returned.', severity: 'warning' });
      }
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setBusyKey(null);
    }
  };

  const handleSchedule = async () => {
    try {
      await tenantApi.createReportSchedule({
        ...schedForm,
        recipients: schedForm.recipients.split(',').map((r) => r.trim()).filter(Boolean),
      });
      setToast({ open: true, message: 'Scheduled report created.', severity: 'success' });
      setSchedOpen(false);
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Reports</Typography>
        <Tooltip title={featureOn(SCHEDULE_FEATURE) ? 'Set up automatic scheduled delivery' : 'Scheduled Reports is not enabled on your plan.'}>
          <span>
            <Button
              variant="outlined" startIcon={<ScheduleIcon />}
              onClick={() => setSchedOpen(true)}
              disabled={!featureOn(SCHEDULE_FEATURE)}
            >
              Schedule Report
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Click a format to generate and download a report instantly. Export formats and report types
        depend on your plan features — unavailable options are greyed out.
      </Typography>

      {/* Available report types */}
      <Paper variant="outlined">
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600}>Available Reports</Typography>
        </Box>
        {REPORT_TYPES.map((rt, i) => {
          const typeOff = !typeEnabled(rt.value);
          return (
            <React.Fragment key={rt.value}>
              {i > 0 && <Divider />}
              <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: typeOff ? 0.6 : 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {rt.label}
                    {typeOff && <Chip label="Feature required" size="small" color="default" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{rt.desc}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {FORMATS.map((f) => {
                    const enabled = formatEnabled(rt.value, f.value);
                    const key = `${rt.value}:${f.value}`;
                    const busy = busyKey === key;
                    const reason = disabledReason(rt.value, f.value);
                    return (
                      <Tooltip key={f.value} title={enabled ? `Generate & download ${f.label}` : reason}>
                        <span>
                          <Chip
                            label={busy ? <CircularProgress size={12} /> : f.value.toUpperCase()}
                            size="small"
                            variant={enabled ? 'filled' : 'outlined'}
                            color={enabled ? 'primary' : 'default'}
                            disabled={!enabled || Boolean(busyKey)}
                            onClick={enabled ? () => handleGenerate(rt.value, f.value) : undefined}
                            sx={{ fontSize: 10, minWidth: 52, cursor: enabled ? 'pointer' : 'default' }}
                          />
                        </span>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            </React.Fragment>
          );
        })}
      </Paper>

      {/* Schedule dialog */}
      <Dialog open={schedOpen} onClose={() => setSchedOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Automatic Report</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Schedule Name *" size="small" fullWidth value={schedForm.name}
              onChange={(e) => setSchedForm((f) => ({ ...f, name: e.target.value }))} />
            <FormControl size="small" fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select value={schedForm.type} label="Report Type"
                onChange={(e) => setSchedForm((f) => ({ ...f, type: e.target.value }))}>
                {REPORT_TYPES.map((r) => (
                  <MenuItem key={r.value} value={r.value} disabled={!typeEnabled(r.value)}>
                    {r.label}{!typeEnabled(r.value) ? ' (feature required)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Format</InputLabel>
              <Select value={schedForm.format} label="Format"
                onChange={(e) => setSchedForm((f) => ({ ...f, format: e.target.value }))}>
                {FORMATS.map((f) => (
                  <MenuItem key={f.value} value={f.value} disabled={!featureOn(FORMAT_FEATURE[f.value])}>
                    {f.label}{!featureOn(FORMAT_FEATURE[f.value]) ? ' (not enabled)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Cron Expression"
              size="small" fullWidth
              value={schedForm.cronExpression}
              onChange={(e) => setSchedForm((f) => ({ ...f, cronExpression: e.target.value }))}
              helperText="e.g. '0 8 * * MON' = every Monday at 8am UTC"
            />
            <TextField
              label="Recipients (comma-separated emails)"
              size="small" fullWidth
              value={schedForm.recipients}
              onChange={(e) => setSchedForm((f) => ({ ...f, recipients: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchedOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSchedule} disabled={!schedForm.name}>
            Create Schedule
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
